import { useState } from 'react';
import {
  ArrowLeftRight,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Layers,
  Layers3,
  ListFilter,
  MapPin,
  OctagonAlert,
  PencilRuler,
  ScanBarcode,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DebouncedSearchInput } from '@/components/ui/debounced-search-input';
import { EmptyState } from '@/components/ui/empty-state';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn, titleIfClamped, TRUNCATE_SAFE } from '@/lib/utils';
import { MrsLink } from '@/components/MrsLink';
import { CountChip, CountChipRow } from './CountChip';
import { MisChip } from './MisChip';
import { MrsChip } from './mrs-hover-card';
import { PostingBreakdownPopover } from './PostingBreakdownPopover';
import { ProgressBar, StatusPill } from './PicklistProgressBits';
import type {
  BinInfoDto,
  MisReferenceDto,
  PicklistDto,
  PicklistItemDto,
  PicklistItemGroupDto,
  PicklistPlannedLineDto,
  PostingBreakdownRow,
} from '../types';
import { ReceivingItemType } from '../types';
import {
  adjustAvailability,
  removeAvailability,
  firstLinePerMrs,
  groupBatchQuantity,
  groupLines,
  groupMisReferences,
  groupOverRelease,
  mrsLineFigures,
  itemPickedLine,
  itemQuantityLine,
  itemState,
  itemTargetQty,
  plannedLineStanding,
  qty,
  segments,
} from '../utils/picklistProgress';

/**
 * PW-14, PT-7 — the picklist detail page's Items table: ONE ROW PER ITEM.
 *
 * ## Rows are item groups, not MRS lines (PT-7, the derived model)
 *
 * `picklist.items` is one row per MRS line — the record — and stays that way. The rows here come from
 * `picklist.itemGroups`: the server groups the lines by (NetSuite item, unit of measure), compared exactly
 * (O-22), and rebuilds the groups after every read-side fold, so every figure on a row is the group's. The
 * expansion draws one "For MRS" strip per planned line. The web never builds a group itself, and never draws
 * how the allocation is split across the MRS lines (`items[].bins`, a display bin's `lines[]`): that split is
 * provisional, and the picker chooses the MRS line when they pick.
 *
 * Two figures still come from the lines: the MIS references, which no group field carries, and the in-flight
 * pill's per-batch share, which only the batch contents carry. The id → line map is built ONCE per render.
 *
 * ## What moved out of the row, and what it means (PW-14)
 *
 * * **MIS No. is deleted.** Slip numbers live in PW-26's Posted-to-NetSuite card and its dialog, where a slip
 *   can show what it actually issued. The "Posted MIS" column names the slips only.
 * * **The Bin column derived from `pickingHistories[]`** — every bin ever picked from, historically. The bin
 *   cards in the expansion derive from the display bins + the pick ledger: the bins that hold picked stock
 *   *now*. A bin picked and then adjusted back to zero appears in the old column and not in the new cards.
 *   That is the intended meaning — "where is the picked stock" — so the expansion is **not** a superset of
 *   what it replaced.
 *
 * ## The bin cards read PICKED OVER TOTAL, and that one format is the whole rule
 *
 * Until 2026-09-22 a card rendered only when `currentlyPickedQuantity > 0`, so an item nobody had picked yet
 * showed "no source bins" although its allocation had existed since creation. Every allocated slot is drawn
 * now, and the quantity says which state it is in: `0/1` before the walk, `1/1` after, `4/10` part-way.
 *
 * ⚠️ The denominator is `allocatedQuantity`, and it is honest ONLY because it is labelled as a total. It is a
 * cap, not a record of picks. On a display bin it is the sum over the item's slots at that (bin, lot/serial),
 * or the one slot's own figure on a serial; a pick-time rebalance never changes it, and only Add Items raises
 * it, before picking starts. An adjustment reduces `currentlyPickedQuantity` and never it, so a slot adjusted
 * 2 → 0 reads `0/2`: nothing in hand, two still allocated and back on the shelf. Never render
 * `allocatedQuantity` on its own here.
 */

/**
 * ONE definition for the header strip and every row.
 *
 * These were two identical literals that had to be edited in lockstep. A header whose track list drifts
 * from its rows puts every label over the wrong column and raises nothing — the same silent shape as the
 * `sr-only` grid-child bug, which cost a round of "why is Status missing a header".
 *
 * ⚠️ **"Qty to pick" is a DELIBERATE divergence from the design handoff §5.5** (2026-09-11, owner's call),
 * recorded here rather than drifting silently — the same way the landing table's page-size select is. The
 * prototype folded the target into the progress line; on a picklist taking part of an MRS line that made it
 * indistinguishable from the MRS's own "Requested". The v2 prototype draws this column too.
 */
const ROW_GRID =
  'grid grid-cols-[32px_minmax(170px,2.1fr)_minmax(110px,0.9fr)_minmax(90px,0.6fr)_minmax(180px,1.6fr)_minmax(104px,0.8fr)_170px] items-center gap-x-3';

const FOR_MRS_TIP =
  'The MRS line this item was added for — what it is for and how much of it is picked here. The picker '
  + 'records which MRS each unit is for when they pick; hover the MRS chip for its fulfillment.';

interface PicklistItemsTableProps {
  picklist: PicklistDto;
  /** `issuance.adjust_picklist` — false means NO button, never a disabled one. */
  canAdjust: boolean;
  /** False while any line has picked units (ISS-R-023). */
  canEdit: boolean;
  isEditing: boolean;
  /** The whole item. What Adjust opens is PT-6's. */
  onAdjust: (group: PicklistItemGroupDto) => void;
  /** Remove takes the whole item: every line id of the group, and its code for the confirm (PT-7 A7). */
  onRemove: (picklistItemIds: number[], itemCode: string) => void;
  /**
   * PT-6 — passed only with `issuance.adjust_picklist`: no handler, no Swap serial button. `bin` is the taken
   * display bin, which is exactly one serial slot (its `id` is the slot id).
   */
  onReplaceSerial?: (group: PicklistItemGroupDto, bin: BinInfoDto) => void;
  /** PT-6 — `enabled` false disables Swap serial and shows `reason` on hover. Absent means enabled. */
  replaceAvailability?: { enabled: boolean; reason: string };
  breakdownRows: PostingBreakdownRow[] | null;
  breakdownLoading: boolean;
  breakdownError: string | null;
  onLoadBreakdown: () => void;
}

export function PicklistItemsTable({
  picklist,
  canAdjust,
  canEdit,
  isEditing,
  onAdjust,
  onRemove,
  onReplaceSerial,
  replaceAvailability,
  breakdownRows,
  breakdownLoading,
  breakdownError,
  onLoadBreakdown,
}: PicklistItemsTableProps) {
  // Local state, not a store read in render (86d3d1z40). It matters more here than elsewhere: the
  // settle-poll ticks every 3 seconds for as long as a batch is open, so a whole-store subscription would
  // repaint this table for that entire time. Holds ANCHOR ids, which stay stable: new lines get higher ids,
  // and Remove takes whole groups.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  // The committed query. The input keeps its own keystroke state and reports here 150 ms after typing stops,
  // so a keystroke re-renders the input alone (UI Performance gates 6-7).
  const [query, setQuery] = useState('');

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clearQuery = () => setQuery('');

  const groups = picklist.itemGroups ?? [];
  // Once per render, never once per row (PT-7 G2).
  const linesById = new Map((picklist.items ?? []).map((line) => [line.id, line] as const));
  const trimmed = query.trim();
  const needle = trimmed.toLowerCase();
  const visible = needle ? groups.filter((g) => matchesQuery(g, needle)) : groups;

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-x-3 gap-y-2 space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          Items
          {/* One root per page, not per row. The count is item rows, the server's `totalItems` basis. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                tabIndex={0}
                className="cursor-help rounded-full bg-muted px-2 py-0.5 text-xs font-medium tracking-normal tabular-nums text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {groups.length}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {groups.length} unique {groups.length === 1 ? 'item' : 'items'} on this picklist
            </TooltipContent>
          </Tooltip>
        </CardTitle>
        <DebouncedSearchInput
          value={query}
          onValueChange={setQuery}
          delay={150}
          placeholder="Search item or MRS…"
          ariaLabel="Search items by code, name or MRS number"
          className="w-[260px] max-w-full"
          // `md:` too: the Input primitive sets `md:text-sm`, which would win above 768px.
          inputClassName={cn(
            'h-8 text-[13px] md:text-[13px]',
            needle && 'border-info bg-info/[0.06] shadow-[0_0_0_3px_hsl(var(--info)/0.2)]',
          )}
        />
      </CardHeader>

      {needle && (
        <div
          role="status"
          className="mx-6 mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-info/30 bg-info/[0.08] px-3 py-2"
        >
          <span className="flex items-center gap-2 text-xs text-info">
            <ListFilter className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>
              Filtered — showing <b className="font-semibold">{matchLabel(visible.length, groups.length)}</b>{' '}
              matching “{trimmed}”. Totals above still count every line.
            </span>
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={clearQuery}
            className="h-7 border-info/35 bg-card px-2.5 text-xs text-info hover:bg-info/10 hover:text-info"
          >
            <X className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            Clear filter
          </Button>
        </div>
      )}

      <CardContent className="p-0">
        {/* A narrow column scrolls the table INSIDE its card, never the page (L405). `min-w-fit` keeps every
            band — the header's tint, each row's rule — as wide as the tracks when they overflow. */}
        <div className="overflow-x-auto rounded-b-xl border-t border-border">
          <div className="min-w-fit">
            {/* A grid, not a <table>: the expansion is a full-width block under its row, and a colSpan cell
                inside a table row cannot host one without fighting the column widths above it. */}
            <div className={cn(ROW_GRID, 'bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground')}>
              {/* An in-flow placeholder for the chevron column. NOT a bare `sr-only` span: that is
                  position:absolute, which takes the element OUT of the grid flow, so the labels would each
                  slide one column left of the values they name. */}
              <span aria-hidden><span className="sr-only">Expand</span></span>
              <span>Item</span>
              <span>MRS</span>
              <span>Qty to pick</span>
              <span>Progress</span>
              <span>Posted MIS</span>
              <span>Status</span>
            </div>

            {needle && visible.length === 0 ? (
              <EmptyState
                size="compact"
                title={`No lines match “${trimmed}”.`}
                action={
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={clearQuery}>
                    Clear search
                  </Button>
                }
              />
            ) : (
              visible.map((group) => (
                <ItemRow
                  key={group.id}
                  picklist={picklist}
                  group={group}
                  lines={groupLines(linesById, group)}
                  isExpanded={expanded.has(group.id)}
                  onToggle={() => toggle(group.id)}
                  canAdjust={canAdjust}
                  canEdit={canEdit}
                  isEditing={isEditing}
                  itemCount={groups.length}
                  onAdjust={onAdjust}
                  onRemove={onRemove}
                  onReplaceSerial={onReplaceSerial}
                  replaceAvailability={replaceAvailability}
                  breakdownRows={breakdownRows}
                  breakdownLoading={breakdownLoading}
                  breakdownError={breakdownError}
                  onLoadBreakdown={onLoadBreakdown}
                />
              ))
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Code, name, or any planned line's MRS number — case-insensitive substring, over the rows in hand. */
function matchesQuery(group: PicklistItemGroupDto, needle: string): boolean {
  return (
    (group.itemCode ?? '').toLowerCase().includes(needle)
    || (group.itemName ?? '').toLowerCase().includes(needle)
    || (group.plannedLines ?? []).some((l) => (l.mrsNumber ?? '').toLowerCase().includes(needle))
  );
}

const matchLabel = (visible: number, total: number): string =>
  `${visible} of ${total} ${total === 1 ? 'line' : 'lines'}`;

interface ItemRowProps extends Omit<PicklistItemsTableProps, 'picklist'> {
  picklist: PicklistDto;
  group: PicklistItemGroupDto;
  /** The group's `items[]` rows, for the two per-line figures (MIS references). */
  lines: PicklistItemDto[];
  isExpanded: boolean;
  onToggle: () => void;
  itemCount: number;
}

function ItemRow({
  picklist,
  group,
  lines,
  isExpanded,
  onToggle,
  canAdjust,
  canEdit,
  isEditing,
  itemCount,
  onAdjust,
  onRemove,
  onReplaceSerial,
  replaceAvailability,
  breakdownRows,
  breakdownLoading,
  breakdownError,
  onLoadBreakdown,
}: ItemRowProps) {
  const unit = group.unitOfMeasure ?? '';
  const unitWord = unit || 'units';
  const plannedLines = group.plannedLines ?? [];
  const segs = segments({
    toPick: group.quantityToPick,
    picked: group.quantityPicked,
    posted: group.quantityPosted,
    inflight: group.postingQuantity,
  });
  const over = groupOverRelease(group);
  const adjust = adjustAvailability(picklist, group, canAdjust);
  const remove = removeAvailability(itemCount, lines.some((l) => l.pickingHistories.length > 0));
  const quantityLine = itemQuantityLine(group);
  const nameTitle = `${group.itemCode} · ${group.itemName}`;
  const taken = (group.bins ?? []).filter((b) => b.takenBy);
  const lineIds = new Set(plannedLines.map((l) => l.id));

  const progress = (
    <div className="max-w-xs space-y-1">
      {/* The bar's TOOLTIP keeps both numbers (`quantityLine`); the visible line drops the target, which has
          its own column. Repeating it here was what made the row read as two competing figures. */}
      <ProgressBar segs={segs} tooltip={quantityLine} />
      <p className="text-xs tabular-nums text-muted-foreground">{itemPickedLine(group)}</p>
    </div>
  );

  return (
    <div className="border-b border-border last:border-b-0">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={`${group.itemCode} — ${isExpanded ? 'hide' : 'show'} source bins`}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        className={cn(
          ROW_GRID,
          'w-full cursor-pointer px-3 py-2.5 text-left hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        )}
      >
        <span className="flex h-6 w-6 items-center justify-center text-muted-foreground" aria-hidden>
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>

        <div className="min-w-0">
          {/* The CODE never wraps: an item code broken mid-token ("2PP-HAR-" / "HDBD6P-B") is unreadable and
              unsearchable. The chip shrinks and the name clamps instead. */}
          <div className="flex items-center gap-1.5">
            <span
              className="truncate whitespace-nowrap font-mono text-sm font-semibold"
              onPointerEnter={(e) => titleIfClamped(e.currentTarget, nameTitle)}
            >
              {group.itemCode}
            </span>
            <TrackingChip itemType={group.itemType} />
          </div>
          {/* Two lines tell most descriptions apart, not all: these are uncapped NetSuite spec strings
              (86d48d39v), and the part cut is often the dimension or grade. So a CLAMPED name carries the full
              text as a native title, measured on pointer enter — never a Radix Tooltip per row (the 239 ms
              lesson), and never a title on a name that fits (UI-GUIDELINES §8).
              No `cursor-help`: the whole row is clickable, and overriding the pointer would say it is not. */}
          <p
            className="line-clamp-2 text-xs leading-snug text-muted-foreground"
            onPointerEnter={(e) => titleIfClamped(e.currentTarget, nameTitle)}
          >
            {group.itemName}
          </p>
        </div>

        <MrsCell picklist={picklist} plannedLines={plannedLines} unit={group.unitOfMeasure ?? ''} />

        {/* What THIS picklist asks for. `tabular-nums` so the column reads as a column when you scan it.
            It WRAPS between the number and the unit when the track is too narrow ("746.644" over "LITER").
            The old `truncate whitespace-nowrap` sat on an inline span, where it clips nothing — so a decimal
            quantity with a long unit ran straight over the Progress column. A quantity is never clipped. */}
        <div className="min-w-0">
          <span className="block break-words text-sm leading-tight tabular-nums">{itemTargetQty(group)}</span>
        </div>

        {/* 86d3n1xum — a posted item's Progress cell opens the per-(bin, serial/lot) breakdown. It is the only
            surface carrying FEFO lot splits, lot expiry, the swap badge and its disposition (O-35: kept). */}
        {group.quantityPosted > 0 ? (
          <PostingBreakdownPopover
            item={group}
            rows={breakdownRows?.filter((r) => lineIds.has(r.picklistItemId))}
            loading={breakdownLoading}
            error={breakdownError}
            onOpen={onLoadBreakdown}
            onRetry={onLoadBreakdown}
          >
            <button
              type="button"
              aria-label={`View posting breakdown for ${group.itemCode}`}
              onClick={(e) => e.stopPropagation()}
              className="group relative -mx-1 block w-full max-w-xs cursor-pointer rounded px-1 py-0.5 text-left transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {progress}
              <Layers3 className="pointer-events-none absolute right-1 top-1 h-3 w-3 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            </button>
          </PostingBreakdownPopover>
        ) : (
          progress
        )}

        {/* Which slip(s) this item went out on, from its lines, each slip once: two lines of one item can go
            out on one slip, and each line then carries a reference to it. */}
        <MisCell refs={groupMisReferences(lines)} unit={group.unitOfMeasure ?? ''} />

        <div className="flex flex-col items-start gap-1">
          <StatusPill state={itemState(group)} />
          {over > 0 && <StatusPill state={overPill(plannedLines, over, unitWord)} />}
          {/* One pill per open batch, each with its own batch's share: a group's lines are often in different
              batches (Post refuses a selection spanning requestors), so the group's total would be wrong on
              each. The label's `{qty} in #{id}` shape is what makes `StatusPill` spin it. */}
          {(group.postingBatchIds ?? []).map((batchId) => {
            const share = groupBatchQuantity(picklist, group, batchId);
            if (share <= 0) return null;
            return (
              <StatusPill
                key={batchId}
                state={{
                  tone: 'info',
                  label: `${qty(share)} in #${batchId}`,
                  tooltip:
                    `${qty(share)} ${unitWord} are posting to NetSuite in batch #${batchId}. Locked: you cannot `
                    + 'adjust or re-post them, and the next batch excludes them.',
                }}
              />
            );
          })}
          {taken.length > 0 && (
            <StatusPill
              className="whitespace-nowrap"
              state={{
                tone: 'destructive',
                label: taken.length === 1 ? '1 needs a swap' : `${taken.length} need a swap`,
                tooltip:
                  'A serial allocated here was picked on another picklist. Swap it from the item’s bin card, '
                  + 'or on the handheld.',
              }}
            />
          )}
        </div>
      </div>

      {isExpanded && (
        // 3px Capitol Red accent down the left, matching the landing's expanded sub-rows.
        <div className="border-l-[3px] border-l-primary/30 bg-muted/30 px-3 py-2.5">
          <div className="flex flex-col gap-1.5">
            {plannedLines.map((line) => (
              <ForMrsStrip key={line.id} picklist={picklist} line={line} unit={unit} />
            ))}
          </div>

          <div className="mt-2.5 space-y-2 px-2.5">
            <div className="flex items-center justify-between gap-2">
              {/* The heading follows the tense of what is under it. "Where it came from" over a list of
                  bins nobody has visited yet reads as a claim that the stock has already moved. */}
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" aria-hidden />
                {(group.bins ?? []).some((b) => b.currentlyPickedQuantity > 0)
                  ? 'Where it came from'
                  : 'Where to pick it from'}
              </p>
              <div className="flex items-center gap-1.5">
                {/* No permission ⇒ no button. A disabled control here would advertise a capability the
                    role does not have. */}
                {canAdjust && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={adjust.enabled ? -1 : 0}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8"
                          disabled={!adjust.enabled}
                          aria-label={`Adjust ${group.itemCode}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onAdjust(group);
                          }}
                        >
                          <PencilRuler className="mr-1.5 h-3.5 w-3.5" />
                          Adjust
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">{adjust.reason}</TooltipContent>
                  </Tooltip>
                )}
                {/* Remove takes the whole item — every line id of the group in one request. "The last item"
                    counts groups; the server's own guard counts lines, so it passes whenever another item
                    remains. */}
                {canEdit && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={remove.enabled ? -1 : 0}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          disabled={isEditing || !remove.enabled}
                          aria-label={remove.enabled
                            ? `Remove ${group.itemCode} from this picklist`
                            : `Remove ${group.itemCode} — ${remove.reason}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemove(plannedLines.map((l) => l.id), group.itemCode);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {remove.enabled ? `Remove ${group.itemCode}` : remove.reason}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>

            <BinCards
              group={group}
              onReplaceSerial={onReplaceSerial}
              replaceAvailability={replaceAvailability}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * "Exceeds MRS qty", summed over the item's OVER lines only (the O-20 wording). The figures are the server's
 * refusal basis (`itemOverRelease`, `MisSelectionRules.EnsureWithinMrsRelease`), so the pill can never
 * contradict the 400; the strips below say which line is over.
 */
function overPill(lines: readonly PicklistPlannedLineDto[], over: number, unitWord: string) {
  let headroom = 0;
  let ready = 0;
  for (const line of lines) {
    const f = mrsLineFigures(line);
    if (f.over <= 0) continue;
    headroom += f.headroom;
    ready += f.ready;
  }
  // ISS-R-117 — the line posts up to its headroom; only the excess waits (owner 2026-10-05, F-09).
  return {
    tone: 'destructive' as const,
    label: 'Exceeds MRS qty',
    tooltip:
      `The MRS allows ${qty(headroom)} more ${unitWord} to be issued, but ${qty(ready)} are picked and ready. `
      + `${qty(headroom)} can be posted now; lower the other ${qty(over)} in Adjust.`,
  };
}

/**
 * The tracking-type chip. Bin-tracked items deliberately show none — the absence is the signal.
 *
 * ⚠️ `itemType` reaching here as `InventoryItem` does NOT reliably mean "bin-tracked". The projection reads
 * `MaterialRequestItem.ItemType`, a NOT-NULL int defaulting to 0, so a line whose MRS was never resynced
 * arrives as `InventoryItem` regardless of what it really is. Drawing nothing in that case is the honest
 * outcome: a wrong chip on a serialized item is worse than no chip.
 */
function TrackingChip({ itemType }: { itemType: ReceivingItemType | null }) {
  if (itemType === ReceivingItemType.Serialized) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" tabIndex={0} className="gap-1 px-1.5 py-0 text-[10px] font-medium">
            <ScanBarcode className="h-3 w-3" aria-hidden />
            Serialized
          </Badge>
        </TooltipTrigger>
        <TooltipContent>Each unit is tracked by serial number.</TooltipContent>
      </Tooltip>
    );
  }
  if (itemType === ReceivingItemType.LotNumbered) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" tabIndex={0} className="gap-1 px-1.5 py-0 text-[10px] font-medium">
            <Layers className="h-3 w-3" aria-hidden />
            Lot
          </Badge>
        </TooltipTrigger>
        <TooltipContent>Tracked by lot number.</TooltipContent>
      </Tooltip>
    );
  }
  return null;
}

/**
 * The MRS column (owner, 2026-10-02: "we don't want a cluttered column"). One MRS is its chip, which opens the
 * page's ONE hover card and links to the MRS. Two or more are a single count chip, "N MRS": its card lists each MRS
 * as a link, with who asked and how much of this item is planned for it. Each MRS's fulfilment card opens from its
 * "For MRS" strip in the expanded row, and from the header's chips.
 */
function MrsCell({ picklist, plannedLines, unit }: { picklist: PicklistDto; plannedLines: PicklistPlannedLineDto[]; unit: string }) {
  const perMrs = firstLinePerMrs(plannedLines);
  if (perMrs.length === 0) return <span className="text-xs text-muted-foreground">—</span>;

  if (perMrs.length === 1) {
    const line = perMrs[0];
    return (
      <div className="flex min-w-0">
        <MrsChip
          picklistId={picklist.id}
          picklistCreatedAt={picklist.createdAt}
          materialRequestId={line.materialRequestId}
          mrsNumber={line.mrsNumber}
          requestorFullName={line.requestorFullName}
          departmentName={line.departmentName}
        />
      </div>
    );
  }

  const plannedFor = (mrsNumber: string) =>
    plannedLines.filter((l) => l.mrsNumber === mrsNumber).reduce((sum, l) => sum + (l.quantityPlanned ?? 0), 0);
  return (
    <div className="flex min-w-0">
      <CountChip
        label={`${perMrs.length} MRS`}
        ariaLabel={`${perMrs.length} material requests — show the list`}
        heading={`For ${perMrs.length} material requests`}
        className="text-info"
      >
        {perMrs.map((line) => {
          const planned = plannedFor(line.mrsNumber);
          return (
            <CountChipRow key={line.mrsNumber}>
              <span className="min-w-0">
                <MrsLink mrsId={line.materialRequestId} mrsNumber={line.mrsNumber} className="font-mono text-[11px]" />
                {line.requestorFullName && (
                  <span className="block max-w-[190px] truncate text-[11px] text-muted-foreground" title={line.requestorFullName}>
                    {line.requestorFullName}
                  </span>
                )}
              </span>
              <span className="whitespace-nowrap tabular-nums text-muted-foreground">
                {qty(planned)} {unit}
              </span>
            </CountChipRow>
          );
        })}
      </CountChip>
    </div>
  );
}

/**
 * The slip(s) the item was issued on, or an em dash while it is still unposted. One slip is its number, a link;
 * several (partial posts) collapse into one "N MIS" chip whose card lists them as links (`MisChip`).
 */
function MisCell({ refs, unit }: { refs: MisReferenceDto[]; unit: string }) {
  if (refs.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <div className="flex min-w-0">
      <MisChip refs={refs} unit={unit} />
    </div>
  );
}

/**
 * "For {MRS}" — one per planned line (PT-7 A7): what the line is for and how much of it is picked here.
 *
 * MRS-wide figures (requested, issued from any picklist) are NOT here: they live in the MRS chip's hover card.
 * Two lines of one MRS on one item are told apart by Remarks and End use, which every strip draws — never by
 * an ordinal (OWNER_DECISIONS §C).
 */
function ForMrsStrip({ picklist, line, unit }: { picklist: PicklistDto; line: PicklistPlannedLineDto; unit: string }) {
  const standing = plannedLineStanding(line);
  // Gated on `known`: an MRS line never synced has requested 0, and `over` would then be every picked unit.
  // The strip still draws — it carries no MRS-wide figure — only the warning is withheld.
  const warn = standing.known && standing.over > 0;
  const planned = line.quantityPlanned ?? 0;
  const picked = line.quantityPicked ?? 0;
  const complete = planned > 0 && picked >= planned;
  const requestor = [line.requestorFullName, line.departmentName].filter(Boolean).join(' · ') || '—';
  const endUse = line.netsuiteEndUseName || '—';

  return (
    <div
      role="group"
      aria-label={`For ${line.mrsNumber}`}
      className={cn('rounded-md border bg-card px-2.5 py-1.5', warn ? 'border-destructive/35' : 'border-border/35')}
    >
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5">
        <span className="flex min-w-[150px] flex-[1_1_150px] items-center gap-1.5 whitespace-nowrap leading-none text-muted-foreground">
          {/* Radix roots here are bounded: strips render only in an expanded row. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="flex cursor-help items-center gap-1">
                <ClipboardList className="h-3.5 w-3.5" aria-hidden />
                <span className="text-xs font-medium">For</span>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{FOR_MRS_TIP}</TooltipContent>
          </Tooltip>
          {/* Owner, 2026-10-02 — the strip's MRS number is the hoverable one: the row above may show only a count
              chip, so this is where each MRS's fulfilment card opens (and its link to the MRS). */}
          <MrsChip
            picklistId={picklist.id}
            picklistCreatedAt={picklist.createdAt}
            materialRequestId={line.materialRequestId}
            mrsNumber={line.mrsNumber}
            requestorFullName={line.requestorFullName}
            departmentName={line.departmentName}
          />
        </span>

        <span className="min-w-[140px] flex-[1_1_160px] truncate text-xs text-muted-foreground" title={requestor}>
          {requestor}
        </span>

        <Tooltip>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              // `min-w-fit`, not a fixed floor: at 120px "139.502 / 139.502 LITER" overflowed onto End use.
              className="flex min-w-fit flex-[1_1_120px] cursor-help items-center gap-1.5 whitespace-nowrap leading-none"
            >
              <span className="text-xs text-muted-foreground">Picked</span>
              <span className={cn('text-xs font-semibold tabular-nums', complete ? 'text-success' : 'text-foreground')}>
                {qty(picked)} / {qty(planned)}{unit ? ` ${unit}` : ''}
              </span>
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {qty(picked)} of the {qty(planned)} {unit || 'units'} this picklist plans for {line.mrsNumber} are
            picked for it. The picker records which MRS each unit is for when they pick.
          </TooltipContent>
        </Tooltip>

        <span className="flex min-w-40 flex-[1_1_160px] items-center gap-1.5 leading-none">
          <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">End use</span>
          <span className="truncate text-xs font-semibold leading-normal" title={endUse}>{endUse}</span>
        </span>

        <RemarksFact remarks={line.remarks} />
      </div>

      {warn && (
        <p className="mt-1.5 flex items-start gap-1.5 text-xs leading-normal text-destructive">
          <OctagonAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1">
            {qty(standing.ready)} picked, {qty(standing.roomLeft)} can still post against {line.mrsNumber} — over
            by {qty(standing.over)}. Lower the extra in Adjust.
          </span>
        </p>
      )}
    </div>
  );
}

/**
 * Remarks, one line with the rest on hover. Remarks earns its place because end use frequently does NOT
 * separate two lines of one item: across the 51 cases where an MRS raises the same item twice or more, the
 * department AND end use are identical in 20, and only this free-text field says what each batch is for.
 */
function RemarksFact({ remarks }: { remarks: string | null | undefined }) {
  const text = remarks?.trim() ? remarks : '';
  const body = (
    <span
      tabIndex={text ? 0 : undefined}
      className={cn('flex min-w-60 flex-[2_1_240px] items-center gap-1.5 leading-none', text && 'cursor-help')}
    >
      <span className="shrink-0 text-xs text-muted-foreground">Remarks</span>
      {/* TRUNCATE_SAFE, not `truncate`: italic shears on BOTH axes inside `overflow:hidden`. See the constant. */}
      <span className={cn(TRUNCATE_SAFE, 'text-xs font-semibold italic leading-normal')}>{text || '—'}</span>
    </span>
  );
  if (!text) return body;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{body}</TooltipTrigger>
      <TooltipContent className="max-w-sm whitespace-pre-wrap">{text}</TooltipContent>
    </Tooltip>
  );
}

interface BinCardsProps {
  group: PicklistItemGroupDto;
  onReplaceSerial?: (group: PicklistItemGroupDto, bin: BinInfoDto) => void;
  replaceAvailability?: { enabled: boolean; reason: string };
}

/**
 * Bin provenance, over the item's DISPLAY bins: a lot or untracked item's slots merged by (bin, lot), a
 * serial's own slot. Never a line's own slots — how the allocation splits across MRS lines is provisional.
 *
 * The card answers one question — where the stock physically is — with three fields: bin, lot/serial,
 * quantity. The one state it adds is a TAKEN serial (PT-7 C2): another picklist picked it, so this slot must
 * be swapped.
 */
function BinCards({ group, onReplaceSerial, replaceAvailability }: BinCardsProps) {
  // 0/0 means closed: the server omits most such bins, and the card filter still hides any it sends.
  const bins = (group.bins ?? []).filter((b) => b.allocatedQuantity > 0 || b.currentlyPickedQuantity > 0);

  if (bins.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
        No bins allocated to this line yet.
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-start gap-2">
      {bins.map((bin) => (
        <BinCard
          key={bin.id}
          group={group}
          bin={bin}
          onReplaceSerial={onReplaceSerial}
          replaceAvailability={replaceAvailability}
        />
      ))}
    </div>
  );
}

function BinCard({ group, bin, onReplaceSerial, replaceAvailability }: BinCardsProps & { bin: BinInfoDto }) {
  const taken = bin.takenBy ?? null;
  const done = bin.currentlyPickedQuantity >= bin.allocatedQuantity;
  const serial = bin.inventoryNumber ?? '';
  // The other picklist's number and the server's own time phrase — never a person's name (O-4 b) and never a
  // client-formatted time (PT-2).
  const takenDesc = taken
    ? `${serial || 'This serial'} was picked on ${taken.picklistNumber} ${taken.pickedAtLabel} — swap needed`
    : '';

  return (
    <div
      // `group` so the label is a valid accessible name; a taken card is focusable, which is how a keyboard
      // reaches its alternate row (`:focus-within` reveals it).
      role="group"
      tabIndex={taken ? 0 : -1}
      aria-label={taken ? `${bin.binNumber} · ${serial} — swap needed` : bin.binNumber}
      className={cn(
        'bincard flex min-w-56 flex-1 flex-col rounded-lg border outline-none focus-visible:ring-2 focus-visible:ring-ring',
        taken ? 'bincard--warn border-destructive/45 bg-destructive/5' : 'border-border bg-card',
      )}
    >
      <div className="bc-main flex items-center gap-2.5 px-2.5 py-2">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
          aria-hidden
        >
          <MapPin className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-mono text-xs font-semibold" title={bin.binNumber}>{bin.binNumber}</p>
          {bin.inventoryNumber && (
            <p className="truncate font-mono text-xs text-muted-foreground" title={bin.inventoryNumber}>
              {bin.inventoryNumber}
            </p>
          )}
        </div>
        {/* PICKED OVER TOTAL — see the file header for why the denominator is honest only as a total. */}
        <span
          className={cn(
            'shrink-0 whitespace-nowrap text-xs tabular-nums',
            done ? 'font-semibold' : 'text-muted-foreground',
          )}
        >
          {qty(bin.currentlyPickedQuantity)}/{qty(bin.allocatedQuantity)} {group.unitOfMeasure}
        </span>
        {taken && (
          // The card's label carries its words, and the row that hover/focus reveals is its visible label, so
          // the flag gets no Tooltip of its own (a second popup over that row).
          <span className="swap-flag h-3.5 w-3.5 shrink-0" aria-hidden>
            <TriangleAlert className="h-3.5 w-3.5 text-destructive" />
          </span>
        )}
      </div>

      {taken && (
        <div className="bc-alt">
          <TriangleAlert className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden />
          <p
            className="line-clamp-2 min-w-0 flex-1 text-[11px] leading-[1.3] text-foreground"
            onPointerEnter={(e) => titleIfClamped(e.currentTarget, takenDesc)}
          >
            {takenDesc}
          </p>
          {onReplaceSerial && (
            <SwapSerialButton
              serial={serial}
              availability={replaceAvailability}
              onClick={() => onReplaceSerial(group, bin)}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** PT-6's action, PT-7's button: rendered only when the page passes the handler (the permission). */
function SwapSerialButton({
  serial,
  availability,
  onClick,
}: {
  serial: string;
  availability?: { enabled: boolean; reason: string };
  onClick: () => void;
}) {
  const enabled = availability?.enabled ?? true;
  const button = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={!enabled}
      aria-label={`Swap serial ${serial}`}
      className="h-[26px] shrink-0 gap-1.5 border-destructive/45 bg-card px-2 text-xs font-medium"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <ArrowLeftRight className="h-3 w-3" aria-hidden />
      Swap serial
    </Button>
  );
  if (enabled) return button;
  // The Adjust button's pattern: a disabled button fires no pointer events, so the reason hangs off a
  // focusable wrapper. Bounded — only taken cards in expanded rows.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="shrink-0">{button}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{availability?.reason}</TooltipContent>
    </Tooltip>
  );
}
